/**
 * Coach Analytics Hooks (Phase 21.8.6a)
 *
 * TanStack Query hooks for the Coach Effectiveness analytics page.
 * Fetches adoption metrics, mode distribution, and session trends.
 */

import { useQuery } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';

// ============================================
// QUERY KEYS
// ============================================

export const coachAnalyticsKeys = {
  all: ['coach-analytics'] as const,
  adoption: (days: number) =>
    [...coachAnalyticsKeys.all, 'adoption', days] as const,
};

// ============================================
// TYPES
// ============================================

export interface CoachAdoptionSummary {
  totalSessions: number;
  uniqueOperators: number;
  totalActiveOperators: number;
  completionRate: number | null;
  avgMessagesPerSession: number | null;
  sessionsPerOperatorPerWeek: number | null;
  returnRate: number | null;
  escalationRate: number | null;
  tokenCost: {
    inputTokens: number;
    outputTokens: number;
    cacheReadTokens: number;
    cacheCreationTokens: number;
    estimatedCostUsd: number;
  };
}

export interface ModeDistribution {
  mode: string;
  count: number;
}

export interface SessionTimeSeries {
  date: string;
  sessions: number;
}

export interface CoachAdoptionData {
  summary: CoachAdoptionSummary;
  modeDistribution: ModeDistribution[];
  sessionsOverTime: SessionTimeSeries[];
}

// ============================================
// QUERIES
// ============================================

/**
 * Fetch coach adoption metrics for the analytics page.
 * @param days - Number of days to look back (0 = all time)
 */
export function useCoachAdoption(days: number) {
  const api = useApi();

  return useQuery({
    queryKey: coachAnalyticsKeys.adoption(days),
    queryFn: () =>
      api.get<CoachAdoptionData>(
        `/api/dashboard/coach-analytics?days=${days}`
      ),
    refetchInterval: 5 * 60000, // Refresh every 5 minutes
  });
}
